import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { showWebSocketNotification } from '@shared/utils/websocketNotificationApi';

import { taskKeys } from '@features/tasks/api';
import type { WebSocketEventRouter } from '@/shared/websocket/router';
import type { WebSocketEnvelope } from '@/shared/websocket/types';

const TASK_EVENT_PREFIX = /^task\./;

const TaskEnvelopeSchema = z.object({
  type: z.string(),
  data: z.unknown(),
});

type TaskEventLevel = 'success' | 'info' | 'warning' | 'error';

interface TaskEventMeta {
  translationKey: string;
  level: TaskEventLevel;
  silent?: boolean;
}

const TASK_EVENT_META: Record<string, TaskEventMeta> = {
  task_update: { translationKey: 'notifications.task.updated', level: 'success' },
  'task.created': { translationKey: 'notifications.task.created', level: 'success' },
  'task.updated': { translationKey: 'notifications.task.updated', level: 'success' },
  'task.status.changed': { translationKey: 'notifications.task.statusChanged', level: 'info' },
  'task.deleted': { translationKey: 'notifications.task.deleted', level: 'warning' },
  'task.assigned': { translationKey: 'notifications.task.assigned', level: 'info' },
  'task.unassigned': { translationKey: 'notifications.task.unassigned', level: 'info' },
  'task.assignee.changed': { translationKey: 'notifications.task.assigneeChanged', level: 'info' },
  'task.comment.created': { translationKey: 'notifications.task.generic', level: 'info', silent: true },
  'task.comment.deleted': { translationKey: 'notifications.task.generic', level: 'info', silent: true },
  default: { translationKey: 'notifications.task.generic', level: 'info' },
};

const TASK_QUERY_ROOT = taskKeys.all[0];

function parseTaskNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return null;
}

function extractTaskId(data: unknown): number | null {
  if (data == null) {
    return null;
  }

  if (typeof data === 'number' || typeof data === 'string' || typeof data === 'bigint') {
    return parseTaskNumericId(data);
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const candidates: unknown[] = [record.task_id, record.taskId, record.id];

    if (record.task && typeof record.task === 'object') {
      const nested = record.task as Record<string, unknown>;
      candidates.push(nested.id, nested.task_id, nested.taskId);
    }

    for (const candidate of candidates) {
      const parsed = parseTaskNumericId(candidate);
      if (parsed != null) {
        return parsed;
      }
    }
  }

  return null;
}

export interface TaskEventsDependencies {
  queryClient: QueryClient;
  translate: (key: string, params?: Record<string, unknown>) => string;
}

export function registerTaskWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient, translate }: TaskEventsDependencies,
): () => void {
  const invalidateTaskQueries = (taskId?: number | null) => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => Array.isArray(queryKey) && queryKey[0] === TASK_QUERY_ROOT,
    });

    if (typeof taskId === 'number' && Number.isFinite(taskId)) {
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    }
  };

  const notifyTaskEvent = (eventType: string, taskId?: number | null) => {
    if (taskId == null) {
      return;
    }

    const meta = TASK_EVENT_META[eventType] ?? TASK_EVENT_META.default;
    if (meta.silent) {
      return;
    }

    const content = translate(meta.translationKey, { id: taskId });
    if (!content) {
      return;
    }

    // Используем глобальный notification API для WebSocket
    showWebSocketNotification(meta.level, content, undefined, 4);
  };

  const handleTaskEvent = (event: WebSocketEnvelope) => {
    const result = TaskEnvelopeSchema.safeParse(event);
    if (!result.success) {
      return;
    }

    const { type, data } = result.data;
    const taskId = extractTaskId(data);
    invalidateTaskQueries(taskId);

    const normalizedType = type === 'task_update' ? 'task.updated' : type;
    notifyTaskEvent(normalizedType, taskId);
  };

  const disposeExact = router.on('task_update', handleTaskEvent);
  const disposePrefix = router.onMatch((type) => TASK_EVENT_PREFIX.test(type), handleTaskEvent);

  return () => {
    disposeExact();
    disposePrefix();
  };
}
